import { Edit, Visibility } from '@mui/icons-material'
import { Button, Card, CardContent, CircularProgress, Divider, Grid, Typography } from '@mui/material'
import { GreenText } from 'components'
import * as controllers from 'controllers'
import {
  VehicleDto,
  VehicleCheckInManufacturersLabelPhotoDto,
  VehicleCheckInTirePressureLabelPhotoDto,
  VehicleCheckInImporterDoorLabelPhotoDto,
  VehicleCheckInComboPhotoDto,
  VehicleCheckInClusterWithOdometerPhotoDto,
  VehicleCheckInDriverAirbagPhotoDto,
  VehicleCheckInBackupCamPhotoDto,
  VehicleCheckInAirbagTellTalePhotoDto,
  VehicleCheckInPassengerAirbagPhotoDto,
  VehicleCheckInVINOnDashboardPhotoDto,
  VehicleCheckInRoofPhotoDto,
  VehicleCheckInFrontCornerPhotoDto,
  VehicleCheckInFrontHeadOnPhotoDto,
  VehicleCheckInRearCornerPhotoDto,
  VehicleCheckInRearHeadOnPhotoDto,
  VehicleCheckInCheckInClusterPhotoDto
} from 'dtos'
import { useEffect, useMemo, useState } from 'react'
import VehicleCheckInViewDialog from './VehicleCheckInViewDialog'
import { useDialogState, useLoadingState } from 'hooks'
import ComboPhotoDialog from './checkIn/ComboPhotoDialog'
import { enqueueSnackbar } from 'notistack'
import VehiclePhotoCard from './VehiclePhotoCard'
import ImporterDoorLabelPhotoDialog from './checkIn/ImporterDoorLabelPhotoDialog'
import ManufacturersLabelPhotoDialog from './checkIn/ManufacturersLabelPhotoDialog'
import TirePressureLabelPhotoDialog from './checkIn/TirePressureLabelPhotoDialog'
import RearHeadOnPhotoDialog from './checkIn/RearHeadOnPhotoDialog'
import RearCornerPhotoDialog from './checkIn/RearCornerPhotoDialog'
import FrontHeadOnPhotoDialog from './checkIn/FrontHeadOnPhotoDialog'
import FrontCornerPhotoDialog from './checkIn/FrontCornerPhotoDialog'
import RoofPhotoDialog from './checkIn/RoofPhotoDialog'
import PassengerAirbagPhotoDialog from './checkIn/PassengerAirbagPhotoDialog'
import AirbagTellTalePhotoDialog from './checkIn/AirbagTellTalePhotoDialog'
import BackupCamPhotoDialog from './checkIn/BackupCamPhotoDialog'
import ClusterWithOdometerPhotoDialog from './checkIn/ClusterWithOdometerPhotoDialog'
import CheckInClusterPhotoDialog from './checkIn/CheckInClusterPhotoDialog'
import DriverAirbagPhotoDialog from './checkIn/DriverAirbagPhotoDialog'
import VINOnDashboardDialog from './checkIn/VINOnDashboardPhotoDialog'

interface VehicleCheckInCheckInPhotoCardProps {
  vehicle: VehicleDto
  refreshManufacturerLabel: () => void
  refreshTirePressureLabel: () => void
}

export default function VehicleCheckInCheckInPhotosListCard({
  vehicle,
  refreshManufacturerLabel,
  refreshTirePressureLabel
}: VehicleCheckInCheckInPhotoCardProps) {
  const [loadingState, setLoadingState] = useLoadingState({
    isUpdatingManufacturersLabel: false,
    isDeletingManufacturersLabel: false,
    isUpdatingTirePressureLabel: false,
    isDeletingTirePressureLabel: false,
    isUpdatingImporterDoorLabel: false,
    isDeletingImporterDoorLabel: false,
    isUpdatingComboPhoto: false,
    isDeletingComboPhoto: false,
    isUpdatingCheckInClusterPhoto: false,
    isDeletingCheckInClusterPhoto: false,
    isUpdatingClusterWithOdometerPhoto: false,
    isDeletingClusterWithOdometerPhoto: false,
    isUpdatingDriverAirbagPhoto: false,
    isDeletingDriverAirbagPhoto: false,
    isUpdatingBackupCamPhoto: false,
    isDeletingBackupCamPhoto: false,
    isUpdatingAirbagTellTalePhoto: false,
    isDeletingAirbagTellTalePhoto: false,
    isUpdatingPassengerAirbagPhoto: false,
    isDeletingPassengerAirbagPhoto: false,
    isUpdatingVINOnDashboardPhoto: false,
    isDeletingVINOnDashboardPhoto: false,
    isUpdatingRoofPhoto: false,
    isDeletingRoofPhoto: false,
    isUpdatingFrontCornerPhoto: false,
    isDeletingFrontCornerPhoto: false,
    isUpdatingFrontHeadOnPhoto: false,
    isDeletingFrontHeadOnPhoto: false,
    isUpdatingRearCornerPhoto: false,
    isDeletingRearCornerPhoto: false,
    isUpdatingRearHeadOnPhoto: false,
    isDeletingRearHeadOnPhoto: false
  })

  const [dialogState, setDialogState] = useDialogState({
    manufacturersLabelPhotoDialogOpen: false,
    tirePressureLabelPhotoDialogOpen: false,
    importerDoorLabelPhotoDialogOpen: false,
    comboPhotoDialogOpen: false,
    checkInClusterPhotoDialogOpen: false,
    clusterWithOdometerPhotoDialogOpen: false,
    driverAirbagPhotoDialogOpen: false,
    backupCamPhotoDialogOpen: false,
    airbagTellTalePhotoDialogOpen: false,
    passengerAirbagPhotoDialogOpen: false,
    vinOnDashboardPhotoDialogOpen: false,
    roofPhotoDialogOpen: false,
    frontCornerPhotoDialogOpen: false,
    frontHeadOnPhotoDialogOpen: false,
    rearCornerPhotoDialogOpen: false,
    rearHeadOnPhotoDialogOpen: false,
    vehicleCheckInViewDialogOpen: false
  })

  const [selectedPhotoTypes, setSelectedPhotoTypes] = useState<string | null>(null)
  // const [selectedPhotoType, setSelectedPhotoType] = useState<string>('')

  const togglePhotoTypeSelection = (type: string) => {
    setSelectedPhotoTypes(prevSelected => (prevSelected === type ? null : type))
  }

  const [manufacturersLabelPhoto, setManufacturersLabelPhoto] = useState<VehicleCheckInManufacturersLabelPhotoDto>(
    new VehicleCheckInManufacturersLabelPhotoDto()
  )
  const [tirePressureLabelPhoto, setTirePressureLabelPhoto] = useState<VehicleCheckInTirePressureLabelPhotoDto>(
    new VehicleCheckInTirePressureLabelPhotoDto()
  )
  const [importerDoorLabelPhoto, setImporterDoorLabelPhoto] = useState<VehicleCheckInImporterDoorLabelPhotoDto>(
    new VehicleCheckInImporterDoorLabelPhotoDto()
  )
  const [comboPhoto, setComboPhoto] = useState<VehicleCheckInComboPhotoDto>(new VehicleCheckInComboPhotoDto())
  const [checkInClusterPhoto, setCheckInClusterPhoto] = useState<VehicleCheckInCheckInClusterPhotoDto>(
    new VehicleCheckInCheckInClusterPhotoDto()
  )
  const [clusterWithOdometerPhoto, setClusterWithOdometerPhoto] = useState<VehicleCheckInClusterWithOdometerPhotoDto>(
    new VehicleCheckInClusterWithOdometerPhotoDto()
  )
  const [driverAirbagPhoto, setDriverAirbagPhoto] = useState<VehicleCheckInDriverAirbagPhotoDto>(
    new VehicleCheckInDriverAirbagPhotoDto()
  )
  const [backupCamPhoto, setBackupCamPhoto] = useState<VehicleCheckInBackupCamPhotoDto>(new VehicleCheckInBackupCamPhotoDto())
  const [airbagTellTalePhoto, setAirbagTellTalePhoto] = useState<VehicleCheckInAirbagTellTalePhotoDto>(
    new VehicleCheckInAirbagTellTalePhotoDto()
  )
  const [passengerAirbagPhoto, setPassengerAirbagPhoto] = useState<VehicleCheckInPassengerAirbagPhotoDto>(
    new VehicleCheckInPassengerAirbagPhotoDto()
  )
  const [vinOnDashboardPhoto, setVinOnDashboardPhoto] = useState<VehicleCheckInVINOnDashboardPhotoDto>(
    new VehicleCheckInVINOnDashboardPhotoDto()
  )
  const [roofPhoto, setRoofPhoto] = useState<VehicleCheckInRoofPhotoDto>(new VehicleCheckInRoofPhotoDto())
  const [frontCornerPhoto, setFrontCornerPhoto] = useState<VehicleCheckInFrontCornerPhotoDto>(
    new VehicleCheckInFrontCornerPhotoDto()
  )
  const [frontHeadOnPhoto, setFrontHeadOnPhoto] = useState<VehicleCheckInFrontHeadOnPhotoDto>(
    new VehicleCheckInFrontHeadOnPhotoDto()
  )
  const [rearCornerPhoto, setRearCornerPhoto] = useState<VehicleCheckInRearCornerPhotoDto>(
    new VehicleCheckInRearCornerPhotoDto()
  )
  const [rearHeadOnPhoto, setRearHeadOnPhoto] = useState<VehicleCheckInRearHeadOnPhotoDto>(
    new VehicleCheckInRearHeadOnPhotoDto()
  )

  useEffect(() => {
    if (vehicle.vehicleGuid) {
      controllers.getVehicleCheckInManufacturersLabelPhotoInformation(vehicle.vehicleGuid).then(response => {
        setManufacturersLabelPhoto(response.value)
      })

      controllers.getVehicleCheckInTirePressureLabelPhotoInformation(vehicle.vehicleGuid).then(response => {
        setTirePressureLabelPhoto(response.value)
      })

      controllers.getVehicleCheckInImporterDoorLabelPhotoInformation(vehicle.vehicleGuid).then(response => {
        setImporterDoorLabelPhoto(response.value)
      })

      controllers.getVehicleCheckInComboPhotoInformation(vehicle.vehicleGuid).then(response => {
        setComboPhoto(response.value)
      })

      controllers.getVehicleCheckInCheckInClusterPhotoInformation(vehicle.vehicleGuid).then(response => {
        setCheckInClusterPhoto(response.value)
      })

      controllers.getVehicleCheckInClusterWithOdometerPhotoInformation(vehicle.vehicleGuid).then(response => {
        setClusterWithOdometerPhoto(response.value)
      })

      controllers.getVehicleCheckInDriverAirbagPhotoInformation(vehicle.vehicleGuid).then(response => {
        setDriverAirbagPhoto(response.value)
      })

      controllers.getVehicleCheckInBackupCamPhotoInformation(vehicle.vehicleGuid).then(response => {
        setBackupCamPhoto(response.value)
      })

      controllers.getVehicleCheckInAirbagTellTalePhotoInformation(vehicle.vehicleGuid).then(response => {
        setAirbagTellTalePhoto(response.value)
      })

      controllers.getVehicleCheckInPassengerAirbagPhotoInformation(vehicle.vehicleGuid).then(response => {
        setPassengerAirbagPhoto(response.value)
      })

      controllers.getVehicleCheckInVINOnDashboardPhotoInformation(vehicle.vehicleGuid).then(response => {
        setVinOnDashboardPhoto(response.value)
      })

      controllers.getVehicleCheckInRoofPhotoInformation(vehicle.vehicleGuid).then(response => {
        setRoofPhoto(response.value)
      })

      controllers.getVehicleCheckInFrontCornerPhotoInformation(vehicle.vehicleGuid).then(response => {
        setFrontCornerPhoto(response.value)
      })

      controllers.getVehicleCheckInFrontHeadOnPhotoInformation(vehicle.vehicleGuid).then(response => {
        setFrontHeadOnPhoto(response.value)
      })

      controllers.getVehicleCheckInRearCornerPhotoInformation(vehicle.vehicleGuid).then(response => {
        setRearCornerPhoto(response.value)
      })

      controllers.getVehicleCheckInRearHeadOnPhotoInformation(vehicle.vehicleGuid).then(response => {
        setRearHeadOnPhoto(response.value)
      })
    }
  }, [vehicle])

  const availablePhotoTypes: Array<
    | 'manufacturers-label'
    | 'tire-pressure-label'
    | 'importer-door-label'
    | 'combo'
    | 'check-in-cluster'
    | 'cluster-with-odometer'
    | 'driver-airbag'
    | 'backup-cam'
    | 'airbag-tell-tale'
    | 'passenger-airbag'
    | 'vin-on-dashboard'
    | 'roof'
    | 'front-corner'
    | 'front-head-on'
    | 'rear-corner'
    | 'rear-head-on'
  > = useMemo(() => {
    const types: Array<
      | 'manufacturers-label'
      | 'tire-pressure-label'
      | 'importer-door-label'
      | 'combo'
      | 'check-in-cluster'
      | 'cluster-with-odometer'
      | 'driver-airbag'
      | 'backup-cam'
      | 'airbag-tell-tale'
      | 'passenger-airbag'
      | 'vin-on-dashboard'
      | 'roof'
      | 'front-corner'
      | 'front-head-on'
      | 'rear-corner'
      | 'rear-head-on'
    > = []

    if (manufacturersLabelPhoto.documentGuid) {
      types.push('manufacturers-label')
    }

    if (tirePressureLabelPhoto.documentGuid) {
      types.push('tire-pressure-label')
    }

    if (importerDoorLabelPhoto.documentGuid) {
      types.push('importer-door-label')
    }

    if (comboPhoto.documentGuid) {
      types.push('combo')
    }

    if (checkInClusterPhoto.documentGuid) {
      types.push('check-in-cluster')
    }

    if (clusterWithOdometerPhoto.documentGuid) {
      types.push('cluster-with-odometer')
    }

    if (driverAirbagPhoto.documentGuid) {
      types.push('driver-airbag')
    }

    if (backupCamPhoto.documentGuid) {
      types.push('backup-cam')
    }

    if (airbagTellTalePhoto.documentGuid) {
      types.push('airbag-tell-tale')
    }

    if (passengerAirbagPhoto.documentGuid) {
      types.push('passenger-airbag')
    }

    if (vinOnDashboardPhoto.documentGuid) {
      types.push('vin-on-dashboard')
    }

    if (roofPhoto.documentGuid) {
      types.push('roof')
    }

    if (frontCornerPhoto.documentGuid) {
      types.push('front-corner')
    }

    if (frontHeadOnPhoto.documentGuid) {
      types.push('front-head-on')
    }

    if (rearCornerPhoto.documentGuid) {
      types.push('rear-corner')
    }

    if (rearHeadOnPhoto.documentGuid) {
      types.push('rear-head-on')
    }
    return types
  }, [
    airbagTellTalePhoto.documentGuid,
    backupCamPhoto.documentGuid,
    checkInClusterPhoto.documentGuid,
    clusterWithOdometerPhoto.documentGuid,
    comboPhoto.documentGuid,
    driverAirbagPhoto.documentGuid,
    frontCornerPhoto.documentGuid,
    frontHeadOnPhoto.documentGuid,
    importerDoorLabelPhoto.documentGuid,
    manufacturersLabelPhoto.documentGuid,
    passengerAirbagPhoto.documentGuid,
    rearCornerPhoto.documentGuid,
    rearHeadOnPhoto.documentGuid,
    roofPhoto.documentGuid,
    tirePressureLabelPhoto.documentGuid,
    vinOnDashboardPhoto.documentGuid
  ])

  const [isGettingCheckInPhotos, setIsGettingCheckInPhotos] = useState<boolean>(false)

  return (
    <>
      <VehicleCheckInViewDialog
        key={selectedPhotoTypes}
        photoType={
          selectedPhotoTypes as
            | 'manufacturers-label'
            | 'tire-pressure-label'
            | 'importer-door-label'
            | 'combo'
            | 'check-in-cluster'
            | 'cluster-with-odometer'
            | 'driver-airbag'
            | 'backup-cam'
            | 'airbag-tell-tale'
            | 'passenger-airbag'
            | 'vin-on-dashboard'
            | 'roof'
            | 'front-corner'
            | 'front-head-on'
            | 'rear-corner'
            | 'rear-head-on'
        }
        photoTypes={availablePhotoTypes}
        vehicleGuid={vehicle.vehicleGuid!}
        onClose={() => {
          setDialogState('vehicleCheckInViewDialogOpen', false)
          setSelectedPhotoTypes(null)
        }}
        open={dialogState.vehicleCheckInViewDialogOpen}
      />

      <ManufacturersLabelPhotoDialog
        initialValues={manufacturersLabelPhoto}
        isLoading={loadingState.isDeletingManufacturersLabel || loadingState.isUpdatingManufacturersLabel}
        onDelete={() => {
          setLoadingState('isDeletingManufacturersLabel', true)
          controllers
            .deleteVehicleCheckInManufacturersLabelPhoto(vehicle.vehicleGuid!)
            .then(response => {
              enqueueSnackbar("Manufacturer's Label Deleted Successfully!", { variant: 'success' })
              setManufacturersLabelPhoto(response.value)
              setDialogState('manufacturersLabelPhotoDialogOpen', false)
              refreshManufacturerLabel()
            })
            .finally(() => {
              setLoadingState('isDeletingManufacturersLabel', false)
            })
        }}
        onSave={values => {
          setLoadingState('isUpdatingManufacturersLabel', true)
          controllers
            .updateVehicleCheckInManufacturersLabelPhotoInformation(vehicle.vehicleGuid!, values)
            .then(response => {
              enqueueSnackbar("Manufacturer's Label Saved Successfully!", { variant: 'success' })
              setManufacturersLabelPhoto(response.value)
              setDialogState('manufacturersLabelPhotoDialogOpen', false)
              refreshManufacturerLabel()
            })
            .finally(() => {
              setLoadingState('isUpdatingManufacturersLabel', false)
            })
        }}
        open={dialogState.manufacturersLabelPhotoDialogOpen}
        onClose={() => {
          setDialogState('manufacturersLabelPhotoDialogOpen', false)
        }}
        vehicleGuid={vehicle.vehicleGuid!}
      />

      <TirePressureLabelPhotoDialog
        initialValues={tirePressureLabelPhoto}
        isLoading={loadingState.isDeletingTirePressureLabel || loadingState.isUpdatingTirePressureLabel}
        onClose={() => {
          setDialogState('tirePressureLabelPhotoDialogOpen', false)
        }}
        onDelete={() => {
          setLoadingState('isDeletingTirePressureLabel', true)
          controllers
            .deleteVehicleCheckInTirePressureLabelPhoto(vehicle.vehicleGuid!)
            .then(response => {
              enqueueSnackbar('Tire Pressure Label Photo Deleted Successfully!', { variant: 'success' })
              setTirePressureLabelPhoto(response.value)
              setDialogState('tirePressureLabelPhotoDialogOpen', false)
              refreshTirePressureLabel()
            })
            .finally(() => {
              setLoadingState('isDeletingTirePressureLabel', false)
            })
        }}
        onSave={values => {
          setLoadingState('isUpdatingTirePressureLabel', true)
          controllers
            .updateVehicleCheckInTirePressureLabelPhotoInformation(vehicle.vehicleGuid!, values)
            .then(response => {
              enqueueSnackbar('Tire Pressure Label Saved Successfully!', { variant: 'success' })
              setTirePressureLabelPhoto(response.value)
              setDialogState('tirePressureLabelPhotoDialogOpen', false)
              refreshTirePressureLabel()
            })
            .finally(() => {
              setLoadingState('isUpdatingTirePressureLabel', false)
            })
        }}
        open={dialogState.tirePressureLabelPhotoDialogOpen}
        vehicleGuid={vehicle.vehicleGuid!}
      />

      <ImporterDoorLabelPhotoDialog
        initialValues={importerDoorLabelPhoto}
        isLoading={loadingState.isDeletingImporterDoorLabel || loadingState.isUpdatingImporterDoorLabel}
        onSave={values => {
          setLoadingState('isUpdatingImporterDoorLabel', true)
          controllers
            .updateVehicleCheckInImporterDoorLabelPhotoInformation(vehicle.vehicleGuid!, values)
            .then(response => {
              enqueueSnackbar('Importer Door Label Saved Successfully!', { variant: 'success' })
              setImporterDoorLabelPhoto(response.value)
              setDialogState('importerDoorLabelPhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isUpdatingImporterDoorLabel', false)
            })
        }}
        onDelete={() => {
          setLoadingState('isDeletingImporterDoorLabel', true)
          controllers
            .deleteVehicleCheckInImporterDoorLabelPhoto(vehicle.vehicleGuid!)
            .then(response => {
              enqueueSnackbar('Importer Door Label Deleted Successfully!', { variant: 'success' })
              setImporterDoorLabelPhoto(response.value)
              setDialogState('importerDoorLabelPhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isDeletingImporterDoorLabel', false)
            })
        }}
        open={dialogState.importerDoorLabelPhotoDialogOpen}
        onClose={() => {
          setDialogState('importerDoorLabelPhotoDialogOpen', false)
        }}
        vehicleGuid={vehicle.vehicleGuid!}
      />

      <ComboPhotoDialog
        initialValues={comboPhoto}
        isLoading={loadingState.isDeletingComboPhoto || loadingState.isUpdatingComboPhoto}
        onClose={() => {
          setDialogState('comboPhotoDialogOpen', false)
        }}
        onDelete={() => {
          setLoadingState('isDeletingComboPhoto', true)
          controllers
            .deleteVehicleCheckInComboPhoto(vehicle.vehicleGuid!)
            .then(response => {
              enqueueSnackbar('Combo Photo Deleted Successfully!', { variant: 'success' })
              setComboPhoto(response.value)
              setDialogState('comboPhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isDeletingComboPhoto', false)
            })
        }}
        onSave={values => {
          setLoadingState('isUpdatingComboPhoto', true)
          controllers
            .updateVehicleCheckInComboPhotoInformation(vehicle.vehicleGuid!, values)
            .then(response => {
              enqueueSnackbar('Combo Photo Saved Successfully!', { variant: 'success' })
              setComboPhoto(response.value)
              setDialogState('comboPhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isUpdatingComboPhoto', false)
            })
        }}
        open={dialogState.comboPhotoDialogOpen}
        vehicleGuid={vehicle.vehicleGuid!}
      />

      <CheckInClusterPhotoDialog
        initialValues={checkInClusterPhoto}
        isLoading={loadingState.isDeletingCheckInClusterPhoto || loadingState.isUpdatingCheckInClusterPhoto}
        onClose={() => {
          setDialogState('checkInClusterPhotoDialogOpen', false)
        }}
        onDelete={() => {
          setLoadingState('isDeletingCheckInClusterPhoto', true)
          controllers
            .deleteVehicleCheckInCheckInClusterPhoto(vehicle.vehicleGuid!)
            .then(response => {
              enqueueSnackbar('Speedometer Before Modification Photo Deleted Successfully!', { variant: 'success' })
              setCheckInClusterPhoto(response.value)
              setDialogState('checkInClusterPhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isDeletingCheckInClusterPhoto', false)
            })
        }}
        onSave={values => {
          setLoadingState('isUpdatingCheckInClusterPhoto', true)
          controllers
            .updateVehicleCheckInCheckInClusterPhotoInformation(vehicle.vehicleGuid!, values)
            .then(response => {
              return controllers.getVehicleCheckInClusterWithOdometerPhotoInformation(vehicle.vehicleGuid!).then(odometerResponse => {
                enqueueSnackbar('Speedometer Before Modification Saved Successfully!', { variant: 'success' })
                setCheckInClusterPhoto(response.value)
                setClusterWithOdometerPhoto(odometerResponse.value)
                setDialogState('checkInClusterPhotoDialogOpen', false)
              })
            })
            .finally(() => {
              setLoadingState('isUpdatingCheckInClusterPhoto', false)
            })
        }}
        open={dialogState.checkInClusterPhotoDialogOpen}
        vehicleGuid={vehicle.vehicleGuid!}
      />

      <ClusterWithOdometerPhotoDialog
        initialValues={clusterWithOdometerPhoto}
        isLoading={loadingState.isDeletingClusterWithOdometerPhoto || loadingState.isUpdatingClusterWithOdometerPhoto}
        onClose={() => {
          setDialogState('clusterWithOdometerPhotoDialogOpen', false)
        }}
        onDelete={() => {
          setLoadingState('isDeletingClusterWithOdometerPhoto', true)
          controllers
            .deleteVehicleCheckInClusterWithOdometerPhoto(vehicle.vehicleGuid!)
            .then(response => {
              enqueueSnackbar('Odometer Before Modification Photo Deleted Successfully!', { variant: 'success' })
              setClusterWithOdometerPhoto(response.value)
              setDialogState('clusterWithOdometerPhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isDeletingClusterWithOdometerPhoto', false)
            })
        }}
        onSave={values => {
          setLoadingState('isUpdatingClusterWithOdometerPhoto', true)
          controllers
            .updateVehicleCheckInClusterWithOdometerPhotoInformation(vehicle.vehicleGuid!, values)
            .then(response => {
              enqueueSnackbar('Odometer Before Modification Saved Successfully!', { variant: 'success' })
              setClusterWithOdometerPhoto(response.value)
              setDialogState('clusterWithOdometerPhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isUpdatingClusterWithOdometerPhoto', false)
            })
        }}
        open={dialogState.clusterWithOdometerPhotoDialogOpen}
        vehicleGuid={vehicle.vehicleGuid!}
      />

      <DriverAirbagPhotoDialog
        initialValues={driverAirbagPhoto}
        isLoading={loadingState.isDeletingDriverAirbagPhoto || loadingState.isUpdatingDriverAirbagPhoto}
        onClose={() => {
          setDialogState('driverAirbagPhotoDialogOpen', false)
        }}
        onDelete={() => {
          setLoadingState('isDeletingDriverAirbagPhoto', true)
          controllers
            .deleteVehicleCheckInDriverAirbagPhoto(vehicle.vehicleGuid!)
            .then(response => {
              enqueueSnackbar('Driver Airbag Photo Deleted Successfully!', { variant: 'success' })
              setDriverAirbagPhoto(response.value)
              setDialogState('driverAirbagPhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isDeletingDriverAirbagPhoto', false)
            })
        }}
        onSave={values => {
          setLoadingState('isUpdatingDriverAirbagPhoto', true)
          controllers
            .updateVehicleCheckInDriverAirbagPhotoInformation(vehicle.vehicleGuid!, values)
            .then(response => {
              enqueueSnackbar('Driver Airbag Saved Successfully!', { variant: 'success' })
              setDriverAirbagPhoto(response.value)
              setDialogState('driverAirbagPhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isUpdatingDriverAirbagPhoto', false)
            })
        }}
        open={dialogState.driverAirbagPhotoDialogOpen}
        vehicleGuid={vehicle.vehicleGuid!}
      />

      <BackupCamPhotoDialog
        initialValues={backupCamPhoto}
        isLoading={loadingState.isDeletingBackupCamPhoto || loadingState.isUpdatingBackupCamPhoto}
        onClose={() => {
          setDialogState('backupCamPhotoDialogOpen', false)
        }}
        onDelete={() => {
          setLoadingState('isDeletingBackupCamPhoto', true)
          controllers
            .deleteVehicleCheckInBackupCamPhoto(vehicle.vehicleGuid!)
            .then(response => {
              enqueueSnackbar('Backup Cam Photo Deleted Successfully!', { variant: 'success' })
              setBackupCamPhoto(response.value)
              setDialogState('backupCamPhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isDeletingBackupCamPhoto', false)
            })
        }}
        onSave={values => {
          setLoadingState('isUpdatingBackupCamPhoto', true)
          controllers
            .updateVehicleCheckInBackupCamPhotoInformation(vehicle.vehicleGuid!, values)
            .then(response => {
              enqueueSnackbar('Backup Cam Saved Successfully!', { variant: 'success' })
              setBackupCamPhoto(response.value)
              setDialogState('backupCamPhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isUpdatingBackupCamPhoto', false)
            })
        }}
        open={dialogState.backupCamPhotoDialogOpen}
        vehicleGuid={vehicle.vehicleGuid!}
      />

      <AirbagTellTalePhotoDialog
        initialValues={airbagTellTalePhoto}
        isLoading={loadingState.isDeletingAirbagTellTalePhoto || loadingState.isUpdatingAirbagTellTalePhoto}
        onClose={() => {
          setDialogState('airbagTellTalePhotoDialogOpen', false)
        }}
        onDelete={() => {
          setLoadingState('isDeletingAirbagTellTalePhoto', true)
          controllers
            .deleteVehicleCheckInAirbagTellTalePhoto(vehicle.vehicleGuid!)
            .then(response => {
              enqueueSnackbar('Airbag Tell-Tale Photo Deleted Successfully!', { variant: 'success' })
              setAirbagTellTalePhoto(response.value)
              setDialogState('airbagTellTalePhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isDeletingAirbagTellTalePhoto', false)
            })
        }}
        onSave={values => {
          setLoadingState('isUpdatingAirbagTellTalePhoto', true)
          controllers
            .updateVehicleCheckInAirbagTellTalePhotoInformation(vehicle.vehicleGuid!, values)
            .then(response => {
              enqueueSnackbar('Airbag Tell-Tale Saved Successfully!', { variant: 'success' })
              setAirbagTellTalePhoto(response.value)
              setDialogState('airbagTellTalePhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isUpdatingAirbagTellTalePhoto', false)
            })
        }}
        open={dialogState.airbagTellTalePhotoDialogOpen}
        vehicleGuid={vehicle.vehicleGuid!}
      />

      <PassengerAirbagPhotoDialog
        initialValues={passengerAirbagPhoto}
        isLoading={loadingState.isDeletingPassengerAirbagPhoto || loadingState.isUpdatingPassengerAirbagPhoto}
        onClose={() => {
          setDialogState('passengerAirbagPhotoDialogOpen', false)
        }}
        onDelete={() => {
          setLoadingState('isDeletingPassengerAirbagPhoto', true)
          controllers
            .deleteVehicleCheckInPassengerAirbagPhoto(vehicle.vehicleGuid!)
            .then(response => {
              enqueueSnackbar('Passenger Airbag Photo Deleted Successfully!', { variant: 'success' })
              setPassengerAirbagPhoto(response.value)
              setDialogState('passengerAirbagPhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isDeletingPassengerAirbagPhoto', false)
            })
        }}
        onSave={values => {
          setLoadingState('isUpdatingPassengerAirbagPhoto', true)
          controllers
            .updateVehicleCheckInPassengerAirbagPhotoInformation(vehicle.vehicleGuid!, values)
            .then(response => {
              enqueueSnackbar('Passenger Airbag Saved Successfully!', { variant: 'success' })
              setPassengerAirbagPhoto(response.value)
              setDialogState('passengerAirbagPhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isUpdatingPassengerAirbagPhoto', false)
            })
        }}
        open={dialogState.passengerAirbagPhotoDialogOpen}
        vehicleGuid={vehicle.vehicleGuid!}
      />

      <VINOnDashboardDialog
        initialValues={vinOnDashboardPhoto}
        isLoading={loadingState.isDeletingVINOnDashboardPhoto || loadingState.isUpdatingVINOnDashboardPhoto}
        onClose={() => {
          setDialogState('vinOnDashboardPhotoDialogOpen', false)
        }}
        onDelete={() => {
          setLoadingState('isDeletingVINOnDashboardPhoto', true)
          controllers
            .deleteVehicleCheckInVINOnDashboardPhoto(vehicle.vehicleGuid!)
            .then(response => {
              enqueueSnackbar('VIN on Dashboard Photo Deleted Successfully!', { variant: 'success' })
              setVinOnDashboardPhoto(response.value)
              setDialogState('vinOnDashboardPhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isDeletingVINOnDashboardPhoto', false)
            })
        }}
        onSave={values => {
          setLoadingState('isUpdatingVINOnDashboardPhoto', true)
          controllers
            .updateVehicleCheckInVINOnDashboardPhotoInformation(vehicle.vehicleGuid!, values)
            .then(response => {
              enqueueSnackbar('VIN on Dashboard Saved Successfully!', { variant: 'success' })
              setVinOnDashboardPhoto(response.value)
              setDialogState('vinOnDashboardPhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isUpdatingVINOnDashboardPhoto', false)
            })
        }}
        open={dialogState.vinOnDashboardPhotoDialogOpen}
        vehicleGuid={vehicle.vehicleGuid!}
      />

      <RoofPhotoDialog
        initialValues={roofPhoto}
        isLoading={loadingState.isDeletingRoofPhoto || loadingState.isUpdatingRoofPhoto}
        onClose={() => {
          setDialogState('roofPhotoDialogOpen', false)
        }}
        onDelete={() => {
          setLoadingState('isDeletingRoofPhoto', true)
          controllers
            .deleteVehicleCheckInRoofPhoto(vehicle.vehicleGuid!)
            .then(response => {
              enqueueSnackbar('Roof Photo Deleted Successfully!', { variant: 'success' })
              setRoofPhoto(response.value)
              setDialogState('roofPhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isDeletingRoofPhoto', false)
            })
        }}
        onSave={values => {
          setLoadingState('isUpdatingRoofPhoto', true)
          controllers
            .updateVehicleCheckInRoofPhotoInformation(vehicle.vehicleGuid!, values)
            .then(response => {
              enqueueSnackbar('Roof Saved Successfully!', { variant: 'success' })
              setRoofPhoto(response.value)
              setDialogState('roofPhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isUpdatingRoofPhoto', false)
            })
        }}
        open={dialogState.roofPhotoDialogOpen}
        vehicleGuid={vehicle.vehicleGuid!}
      />

      <FrontCornerPhotoDialog
        initialValues={frontCornerPhoto}
        isLoading={loadingState.isDeletingFrontCornerPhoto || loadingState.isUpdatingFrontCornerPhoto}
        onClose={() => {
          setDialogState('frontCornerPhotoDialogOpen', false)
        }}
        onDelete={() => {
          setLoadingState('isDeletingFrontCornerPhoto', true)
          controllers
            .deleteVehicleCheckInFrontCornerPhoto(vehicle.vehicleGuid!)
            .then(response => {
              enqueueSnackbar('Front Corner Photo Deleted Successfully!', { variant: 'success' })
              setFrontCornerPhoto(response.value)
              setDialogState('frontCornerPhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isDeletingFrontCornerPhoto', false)
            })
        }}
        onSave={values => {
          setLoadingState('isUpdatingFrontCornerPhoto', true)
          controllers
            .updateVehicleCheckInFrontCornerPhotoInformation(vehicle.vehicleGuid!, values)
            .then(response => {
              enqueueSnackbar('Front Corner Saved Successfully!', { variant: 'success' })
              setFrontCornerPhoto(response.value)
              setDialogState('frontCornerPhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isUpdatingFrontCornerPhoto', false)
            })
        }}
        open={dialogState.frontCornerPhotoDialogOpen}
        vehicleGuid={vehicle.vehicleGuid!}
      />

      <FrontHeadOnPhotoDialog
        initialValues={frontHeadOnPhoto}
        isLoading={loadingState.isDeletingFrontHeadOnPhoto || loadingState.isUpdatingFrontHeadOnPhoto}
        onClose={() => {
          setDialogState('frontHeadOnPhotoDialogOpen', false)
        }}
        onDelete={() => {
          setLoadingState('isDeletingFrontHeadOnPhoto', true)
          controllers
            .deleteVehicleCheckInFrontHeadOnPhoto(vehicle.vehicleGuid!)
            .then(response => {
              enqueueSnackbar('Front Head On Photo Deleted Successfully!', { variant: 'success' })
              setFrontHeadOnPhoto(response.value)
              setDialogState('frontHeadOnPhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isDeletingFrontHeadOnPhoto', false)
            })
        }}
        onSave={values => {
          setLoadingState('isUpdatingFrontHeadOnPhoto', true)
          controllers
            .updateVehicleCheckInFrontHeadOnPhotoInformation(vehicle.vehicleGuid!, values)
            .then(response => {
              enqueueSnackbar('Front Head On Saved Successfully!', { variant: 'success' })
              setFrontHeadOnPhoto(response.value)
              setDialogState('frontHeadOnPhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isUpdatingFrontHeadOnPhoto', false)
            })
        }}
        open={dialogState.frontHeadOnPhotoDialogOpen}
        vehicleGuid={vehicle.vehicleGuid!}
      />

      <RearCornerPhotoDialog
        initialValues={rearCornerPhoto}
        isLoading={loadingState.isDeletingRearCornerPhoto || loadingState.isUpdatingRearCornerPhoto}
        onClose={() => {
          setDialogState('rearCornerPhotoDialogOpen', false)
        }}
        onDelete={() => {
          setLoadingState('isDeletingRearCornerPhoto', true)
          controllers
            .deleteVehicleCheckInRearCornerPhoto(vehicle.vehicleGuid!)
            .then(response => {
              enqueueSnackbar('Rear Corner Photo Deleted Successfully!', { variant: 'success' })
              setRearCornerPhoto(response.value)
              setDialogState('rearCornerPhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isDeletingRearCornerPhoto', false)
            })
        }}
        onSave={values => {
          setLoadingState('isUpdatingRearCornerPhoto', true)
          controllers
            .updateVehicleCheckInRearCornerPhotoInformation(vehicle.vehicleGuid!, values)
            .then(response => {
              enqueueSnackbar('Rear Corner Saved Successfully!', { variant: 'success' })
              setRearCornerPhoto(response.value)
              setDialogState('rearCornerPhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isUpdatingRearCornerPhoto', false)
            })
        }}
        open={dialogState.rearCornerPhotoDialogOpen}
        vehicleGuid={vehicle.vehicleGuid!}
      />

      <RearHeadOnPhotoDialog
        initialValues={rearHeadOnPhoto}
        isLoading={loadingState.isDeletingRearHeadOnPhoto || loadingState.isUpdatingRearHeadOnPhoto}
        onClose={() => {
          setDialogState('rearHeadOnPhotoDialogOpen', false)
        }}
        onDelete={() => {
          setLoadingState('isDeletingRearHeadOnPhoto', true)
          controllers
            .deleteVehicleCheckInRearHeadOnPhoto(vehicle.vehicleGuid!)
            .then(response => {
              enqueueSnackbar('Rear Head On Photo Deleted Successfully!', { variant: 'success' })
              setRearHeadOnPhoto(response.value)
              setDialogState('rearHeadOnPhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isDeletingRearHeadOnPhoto', false)
            })
        }}
        onSave={values => {
          setLoadingState('isUpdatingRearHeadOnPhoto', true)
          controllers
            .updateVehicleCheckInRearHeadOnPhotoInformation(vehicle.vehicleGuid!, values)
            .then(response => {
              enqueueSnackbar('Rear Head On Saved Successfully!', { variant: 'success' })
              setRearHeadOnPhoto(response.value)
              setDialogState('rearHeadOnPhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isUpdatingRearHeadOnPhoto', false)
            })
        }}
        open={dialogState.rearHeadOnPhotoDialogOpen}
        vehicleGuid={vehicle.vehicleGuid!}
      />
      <Card variant='outlined'>
        <CardContent>
          <Grid container spacing={1} alignItems='flex-start'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm={6}>
                <Typography fontWeight='bold' variant='h5'>
                  Check-In Photos
                </Typography>
              </Grid>

              <Grid item xs={12} sm='auto'>
                <Button
                  onClick={() => {
                    if (!selectedPhotoTypes) {
                      enqueueSnackbar('Please select a photo to view.', { variant: 'warning' })
                    } else {
                      setDialogState('vehicleCheckInViewDialogOpen', true)
                    }
                  }}
                  variant='text'
                  endIcon={<Visibility />}
                >
                  VIEW SELECTED
                </Button>
              </Grid>
              <Grid item xs={12} sm='auto'>
                <Button
                  onClick={() => {
                    if (!selectedPhotoTypes) {
                      enqueueSnackbar('Please select a photo to upload/replace.', { variant: 'warning' })
                    } else if (selectedPhotoTypes) {
                      const selectedType = selectedPhotoTypes
                      if (selectedType === 'manufacturers-label') {
                        setDialogState('manufacturersLabelPhotoDialogOpen', true)
                      } else if (selectedType === 'tire-pressure-label') {
                        setDialogState('tirePressureLabelPhotoDialogOpen', true)
                      } else if (selectedType === 'importer-door-label') {
                        setDialogState('importerDoorLabelPhotoDialogOpen', true)
                      } else if (selectedType === 'combo') {
                        setDialogState('comboPhotoDialogOpen', true)
                      } else if (selectedType === 'check-in-cluster') {
                        setDialogState('checkInClusterPhotoDialogOpen', true)
                      } else if (selectedType === 'cluster-with-odometer') {
                        setDialogState('clusterWithOdometerPhotoDialogOpen', true)
                      } else if (selectedType === 'driver-airbag') {
                        setDialogState('driverAirbagPhotoDialogOpen', true)
                      } else if (selectedType === 'backup-cam') {
                        setDialogState('backupCamPhotoDialogOpen', true)
                      } else if (selectedType === 'airbag-tell-tale') {
                        setDialogState('airbagTellTalePhotoDialogOpen', true)
                      } else if (selectedType === 'passenger-airbag') {
                        setDialogState('passengerAirbagPhotoDialogOpen', true)
                      } else if (selectedType === 'vin-on-dashboard') {
                        setDialogState('vinOnDashboardPhotoDialogOpen', true)
                      } else if (selectedType === 'roof') {
                        setDialogState('roofPhotoDialogOpen', true)
                      } else if (selectedType === 'front-corner') {
                        setDialogState('frontCornerPhotoDialogOpen', true)
                      } else if (selectedType === 'front-head-on') {
                        setDialogState('frontHeadOnPhotoDialogOpen', true)
                      } else if (selectedType === 'rear-corner') {
                        setDialogState('rearCornerPhotoDialogOpen', true)
                      } else if (selectedType === 'rear-head-on') {
                        setDialogState('rearHeadOnPhotoDialogOpen', true)
                      } else {
                        enqueueSnackbar('Please select only one photo at a time to upload/replace.', { variant: 'warning' })
                      }
                    }
                  }}
                  variant='text'
                  endIcon={<Edit />}
                >
                  UPLOAD/REPLACE SELECTED
                </Button>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {isGettingCheckInPhotos && (
              <Grid item container alignItems='center' justifyContent='center' sx={{ my: 1 }}>
                <Grid>
                  <CircularProgress color='primary' size={50} />
                </Grid>
              </Grid>
            )}

            <Grid container spacing={1} alignItems='center' justifyContent='center' sx={{ mt: 0.25 }}>
              <Grid item xs={3}>
                <VehiclePhotoCard source='vehicle-check-in' width='165px' imageWidth={150} imageHeight={84.375}
                  complete={Boolean(manufacturersLabelPhoto.documentGuid)}
                  documentGuid={manufacturersLabelPhoto.documentGuid}
                  onClick={() => {
                    togglePhotoTypeSelection('manufacturers-label')
                  }}
                  type='manufacturers-label'
                  vehicleGuid={vehicle.vehicleGuid}
                  warning={manufacturersLabelPhoto.vehicleCheckInIsManufacturersLabelPhotoMissing}
                  selected={selectedPhotoTypes === 'manufacturers-label'}
                />
              </Grid>
              <Grid item xs={3}>
                <VehiclePhotoCard source='vehicle-check-in' width='165px' imageWidth={150} imageHeight={84.375}
                  complete={Boolean(tirePressureLabelPhoto.documentGuid)}
                  documentGuid={tirePressureLabelPhoto.documentGuid}
                  onClick={() => {
                    togglePhotoTypeSelection('tire-pressure-label')
                  }}
                  type='tire-pressure-label'
                  vehicleGuid={vehicle.vehicleGuid}
                  warning={tirePressureLabelPhoto.vehicleCheckInIsTirePressureLabelPhotoMissing}
                  selected={selectedPhotoTypes === 'tire-pressure-label'}
                />
              </Grid>
              <Grid item xs={3}>
                <VehiclePhotoCard source='vehicle-check-in' width='165px' imageWidth={150} imageHeight={84.375}
                  complete={Boolean(importerDoorLabelPhoto.documentGuid)}
                  documentGuid={importerDoorLabelPhoto.documentGuid}
                  onClick={() => {
                    togglePhotoTypeSelection('importer-door-label')
                  }}
                  type='importer-door-label'
                  vehicleGuid={vehicle.vehicleGuid}
                  selected={selectedPhotoTypes === 'importer-door-label'}
                />
              </Grid>
              <Grid item xs={3}>
                <VehiclePhotoCard source='vehicle-check-in' width='165px' imageWidth={150} imageHeight={84.375}
                  complete={Boolean(comboPhoto.documentGuid)}
                  documentGuid={comboPhoto.documentGuid}
                  onClick={() => {
                    togglePhotoTypeSelection('combo')
                  }}
                  type='combo'
                  vehicleGuid={vehicle.vehicleGuid}
                  selected={selectedPhotoTypes === 'combo'}
                />
              </Grid>

              <Grid item xs={3}>
                <VehiclePhotoCard source='vehicle-check-in' width='165px' imageWidth={150} imageHeight={84.375}
                  complete={Boolean(checkInClusterPhoto.documentGuid)}
                  documentGuid={checkInClusterPhoto.documentGuid}
                  onClick={() => {
                    togglePhotoTypeSelection('check-in-cluster')
                  }}
                  type='check-in-cluster'
                  vehicleGuid={vehicle.vehicleGuid}
                  selected={selectedPhotoTypes === 'check-in-cluster'}
                />
              </Grid>
              <Grid item xs={3}>
                <VehiclePhotoCard source='vehicle-check-in' width='165px' imageWidth={150} imageHeight={84.375}
                  complete={Boolean(clusterWithOdometerPhoto.documentGuid)}
                  documentGuid={clusterWithOdometerPhoto.documentGuid}
                  onClick={() => {
                    togglePhotoTypeSelection('cluster-with-odometer')
                  }}
                  type='cluster-with-odometer'
                  vehicleGuid={vehicle.vehicleGuid}
                  selected={selectedPhotoTypes === 'cluster-with-odometer'}
                />
              </Grid>
              <Grid item xs={3}>
                <VehiclePhotoCard source='vehicle-check-in' width='165px' imageWidth={150} imageHeight={84.375}
                  complete={Boolean(driverAirbagPhoto.documentGuid)}
                  documentGuid={driverAirbagPhoto.documentGuid}
                  onClick={() => {
                    togglePhotoTypeSelection('driver-airbag')
                  }}
                  type='driver-airbag'
                  vehicleGuid={vehicle.vehicleGuid}
                  selected={selectedPhotoTypes === 'driver-airbag'}
                />
              </Grid>

              <Grid item xs={3}>
                <VehiclePhotoCard source='vehicle-check-in' width='165px' imageWidth={150} imageHeight={84.375}
                  complete={Boolean(backupCamPhoto.documentGuid)}
                  disabled={backupCamPhoto.vehicleCheckInHasBackupCam === false}
                  documentGuid={backupCamPhoto.documentGuid}
                  onClick={() => {
                    togglePhotoTypeSelection('backup-cam')
                  }}
                  type='backup-cam'
                  vehicleGuid={vehicle.vehicleGuid}
                  selected={selectedPhotoTypes === 'backup-cam'}
                />
              </Grid>
              <Grid item xs={3}>
                <VehiclePhotoCard source='vehicle-check-in' width='165px' imageWidth={150} imageHeight={84.375}
                  complete={Boolean(airbagTellTalePhoto.documentGuid)}
                  documentGuid={airbagTellTalePhoto.documentGuid}
                  onClick={() => {
                    togglePhotoTypeSelection('airbag-tell-tale')
                  }}
                  type='airbag-tell-tale'
                  vehicleGuid={vehicle.vehicleGuid}
                  selected={selectedPhotoTypes === 'airbag-tell-tale'}
                />
              </Grid>
              <Grid item xs={3}>
                <VehiclePhotoCard source='vehicle-check-in' width='165px' imageWidth={150} imageHeight={84.375}
                  complete={Boolean(passengerAirbagPhoto.documentGuid)}
                  disabled={passengerAirbagPhoto.vehicleCheckInHasPassengerAirbag === false}
                  documentGuid={passengerAirbagPhoto.documentGuid}
                  onClick={() => {
                    togglePhotoTypeSelection('passenger-airbag')
                  }}
                  type='passenger-airbag'
                  vehicleGuid={vehicle.vehicleGuid}
                  selected={selectedPhotoTypes === 'passenger-airbag'}
                />
              </Grid>
              <Grid item xs={3}>
                <VehiclePhotoCard source='vehicle-check-in' width='165px' imageWidth={150} imageHeight={84.375}
                  complete={Boolean(vinOnDashboardPhoto.documentGuid)}
                  documentGuid={vinOnDashboardPhoto.documentGuid}
                  onClick={() => {
                    togglePhotoTypeSelection('vin-on-dashboard')
                  }}
                  type='vin-on-dashboard'
                  vehicleGuid={vehicle.vehicleGuid}
                  selected={selectedPhotoTypes === 'vin-on-dashboard'}
                />
              </Grid>

              <Grid item xs={3}>
                <VehiclePhotoCard source='vehicle-check-in' width='165px' imageWidth={150} imageHeight={84.375}
                  complete={Boolean(roofPhoto.documentGuid)}
                  documentGuid={roofPhoto.documentGuid}
                  onClick={() => {
                    togglePhotoTypeSelection('roof')
                  }}
                  type='roof'
                  vehicleGuid={vehicle.vehicleGuid}
                  selected={selectedPhotoTypes === 'roof'}
                />
              </Grid>
              <Grid item xs={3}>
                <VehiclePhotoCard source='vehicle-check-in' width='165px' imageWidth={150} imageHeight={84.375}
                  complete={Boolean(frontCornerPhoto.documentGuid)}
                  documentGuid={frontCornerPhoto.documentGuid}
                  onClick={() => {
                    togglePhotoTypeSelection('front-corner')
                  }}
                  type='front-corner'
                  vehicleGuid={vehicle.vehicleGuid}
                  selected={selectedPhotoTypes === 'front-corner'}
                />
              </Grid>
              <Grid item xs={3}>
                <VehiclePhotoCard source='vehicle-check-in' width='165px' imageWidth={150} imageHeight={84.375}
                  complete={Boolean(frontHeadOnPhoto.documentGuid)}
                  documentGuid={frontHeadOnPhoto.documentGuid}
                  onClick={() => {
                    togglePhotoTypeSelection('front-head-on')
                  }}
                  type='front-head-on'
                  vehicleGuid={vehicle.vehicleGuid}
                  selected={selectedPhotoTypes === 'front-head-on'}
                />
              </Grid>
              <Grid item xs={3}>
                <VehiclePhotoCard source='vehicle-check-in' width='165px' imageWidth={150} imageHeight={84.375}
                  complete={Boolean(rearCornerPhoto.documentGuid)}
                  documentGuid={rearCornerPhoto.documentGuid}
                  onClick={() => {
                    togglePhotoTypeSelection('rear-corner')
                  }}
                  type='rear-corner'
                  vehicleGuid={vehicle.vehicleGuid}
                  selected={selectedPhotoTypes === 'rear-corner'}
                />
              </Grid>

              <Grid item xs={3}>
                <VehiclePhotoCard source='vehicle-check-in' width='165px' imageWidth={150} imageHeight={84.375}
                  complete={Boolean(rearHeadOnPhoto.documentGuid)}
                  documentGuid={rearHeadOnPhoto.documentGuid}
                  onClick={() => {
                    togglePhotoTypeSelection('rear-head-on')
                  }}
                  type='rear-head-on'
                  vehicleGuid={vehicle.vehicleGuid}
                  selected={selectedPhotoTypes === 'rear-head-on'}
                />
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <GreenText variant='outlined'>
                {`**VIEW SELECTED Button:**

The “VIEW SELECTED” button is only available if the user has selected photos to view.

Clicking the “VIEW SELECTED” button will open the View Check In Photo Dialog.

**UPLOAD/REPLACE SELECTED Button:**

The “UPLOAD/REPLACE SELECTED” button is only available if the user has a photo selected to replace.

Clicking the “UPLOAD/REPLACE SELECTED” button will open the Photo Details dialog.

**Manufacturers Label Image:**

Displays the uploaded Manufactures Label Image.

If no image has been uploaded the “No Image” icon will be displayed instead.

Clicking the Manufacturers Label will select the image.

**Tire Pressure Label:**

Displays the uploaded Tire Pressure Label Image.

If no image has been uploaded the “No Image” icon will be displayed instead.

Clicking the Tire Pressure Label will select the image.

**Importer Door Label:**

Displays the uploaded Importer Door Label Image.

If no image has been uploaded the “No Image” icon will be displayed instead.

Clicking the Importer Door Label will select the image.

**Combo:**

Displays the uploaded Combo Image.

If no image has been uploaded the “No Image” icon will be displayed instead.

Clicking the Combo will select the image.

**Speedometer Before Modification:**

Displays the uploaded Speedometer Before Modification image.

If no image has been uploaded the “No Image” icon will be displayed instead.

Clicking the Speedometer Before Modification will select the image.

**Odometer Before Modification:**

Displays the uploaded Odometer Before Modification Image.

If no image has been uploaded the “No Image” icon will be displayed instead.

Clicking the Odometer Before Modification image will select the image.

**Driver Airbag:**

Displays the uploaded Driver Airbag Image.

If no image has been uploaded the “No Image” icon will be displayed instead.

Clicking the Driver Airbag will select the image.

**Backup Cam:**

Displays the uploaded Backup Cam Image.

If no image has been uploaded the “No Image” icon will be displayed instead.

Clicking the Backup Cam will select the image.

**Airbag Tell-Tale:**

Displays the uploaded Airbag Tell-Tale Image.

If no image has been uploaded the “No Image” icon will be displayed instead.

Clicking the Airbag Tell-Tale will select the image.

**Passenger Airbag:**

Displays the uploaded Passenger Airbag Image.

If no image has been uploaded the “No Image” icon will be displayed instead.

Clicking the Passenger Airbag will select the image.

***VIN on Dashboard:***

Displays the uploaded VIN on Dashboard Image.

If no image has been uploaded the “No Image” icon will be displayed instead.

Clicking the VIN on Dashboard will select the image.

**Roof:**

Displays the uploaded Roof Image.

If no image has been uploaded the “No Image” icon will be displayed instead.

Clicking the Roof will select the image.

**Front Corner:**

Displays the uploaded Front Corner Image.

If no image has been uploaded the “No Image” icon will be displayed instead.

Clicking the Front Corner will select the image.

**Front Head On:**

Displays the uploaded Front Head On Image.

If no image has been uploaded the “No Image” icon will be displayed instead.

Clicking the Front Head On will select the image.

**Rear Corner:**

Displays the uploaded Rear Corner Image.

If no image has been uploaded the “No Image” icon will be displayed instead.

Clicking the Rear Corner will select the image.

**Rear Head On:**

Displays the uploaded Rear Head On Image.

If no image has been uploaded the “No Image” icon will be displayed instead.

Clicking the Rear Head On will select the image.`}
              </GreenText>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
